import type {
  MutationFunctionOptions,
  MutationHookOptions,
  OperationVariables,
} from '@apollo/client';
import {FormattedMessage} from '@pinecast/i18n';
import type {DocumentNode, ExecutionResult} from 'graphql';
import * as React from 'react';

import {useMutation} from './useMutation';

type ManagedMutationAction<TData, TVariables extends OperationVariables> = (
  params?: MutationFunctionOptions<TData, TVariables>,
) => Promise<NonNullable<ExecutionResult<TData>['data']>>;

type ErrorState<TData, TVariables extends OperationVariables> = {
  error: React.ReactNode;
  pending: false;
  success: false;
  action: ManagedMutationAction<TData, TVariables>;
};
type PendingState<TData, TVariables extends OperationVariables> = {
  error: null;
  pending: true;
  success: false;
  action: ManagedMutationAction<TData, TVariables>;
};
type SuccessState<TData, TVariables extends OperationVariables> = {
  error: null;
  pending: false;
  success: true;
  action: ManagedMutationAction<TData, TVariables>;
};
type InitialState<TData, TVariables extends OperationVariables> = {
  error: null;
  pending: false;
  success: false;
  action: ManagedMutationAction<TData, TVariables>;
};

export type ManagedMutationResult<
  TData,
  TVariables extends OperationVariables,
> =
  | InitialState<TData, TVariables>
  | PendingState<TData, TVariables>
  | SuccessState<TData, TVariables>
  | ErrorState<TData, TVariables>;

export function useManagedMutation<
  TData extends Record<string, any> = any,
  TVariables extends OperationVariables = OperationVariables,
>(
  query: DocumentNode,
  params?: MutationHookOptions<TData, TVariables>,
): ManagedMutationResult<TData, TVariables> {
  const [pending, setPending] = React.useState(false);
  const [error, setError] = React.useState<React.ReactNode>(null);
  const [success, setSuccess] = React.useState(false);

  const mutation = useMutation<TData, TVariables>(query, params);

  const action = React.useCallback(
    async (
      params?: MutationFunctionOptions<TData, TVariables>,
    ): Promise<ExecutionResult<TData>['data']> => {
      if (pending) {
        return;
      }
      setPending(true);
      setError(null);
      setSuccess(false);
      let result;
      try {
        result = await mutation(params);
      } catch (e) {
        if (e.graphQLErrors?.length) {
          // The server words these in the page's language: the client sends
          // it as `Content-Language`.
          setError(e.graphQLErrors[0].message);
        } else {
          // The request failed, or the answer was not GraphQL (a proxy's
          // error page, say): there are no words from the server to show.
          setError(
            <FormattedMessage
              description="Error message when the network fails"
              defaultMessage="Could not contact Pinecast. Be sure ad blockers are disabled, or try again in Incognito or Private Browsing mode."
            />,
          );
        }
        return;
      } finally {
        setPending(false);
      }
      if (result.errors) {
        setError(result.errors[0].message);
        return;
      }

      setSuccess(true);
      return result.data;
    },
    [mutation, pending],
  );

  return {error, pending, success, action} as ManagedMutationResult<
    TData,
    TVariables
  >;
}
